/**
 * UniApp 存储适配器
 * 使用 uni.setStorage/uni.getStorage 实现跨平台存储
 */

import type { IStorage } from './Storage'

export class UniAppStorage implements IStorage {
  private prefix: string

  constructor(prefix: string = 'csd_') {
    this.prefix = prefix
  }

  /**
   * 获取完整的存储键名
   */
  private getKey(key: string): string {
    return `${this.prefix}${key}`
  }

  /**
   * 获取数据
   */
  getItem<T>(key: string): T | null {
    try {
      const fullKey = this.getKey(key)

      // 使用同步方法以保持一致性
      const value = uni.getStorageSync(fullKey)

      if (!value) {
        return null
      }

      // 尝试解析 JSON
      try {
        return JSON.parse(value) as T
      } catch {
        // JSON 解析失败，返回 null
        return null
      }
    } catch (error) {
      console.error('[UniAppStorage] Get error:', error)
      return null
    }
  }

  /**
   * 设置数据
   */
  setItem<T>(key: string, value: T): void {
    try {
      const fullKey = this.getKey(key)

      // 序列化数据 - 所有值都使用 JSON.stringify
      const serialized = JSON.stringify(value)

      // 使用同步方法
      uni.setStorageSync(fullKey, serialized)
    } catch (error) {
      console.error('[UniAppStorage] Set error:', error)
      throw new Error('Failed to set item')
    }
  }

  /**
   * 删除数据
   */
  removeItem(key: string): void {
    try {
      const fullKey = this.getKey(key)
      uni.removeStorageSync(fullKey)
    } catch (error) {
      console.error('[UniAppStorage] Remove error:', error)
      throw new Error('Failed to remove item')
    }
  }

  /**
   * 清空所有数据
   */
  clear(): void {
    try {
      // 获取所有键
      const info = uni.getStorageInfoSync()

      // 检查返回值
      if (!info || !info.keys) {
        throw new Error('Failed to get storage info')
      }

      const keys = info.keys

      // 只删除带有前缀的键
      keys.forEach((key: string) => {
        if (key.startsWith(this.prefix)) {
          uni.removeStorageSync(key)
        }
      })
    } catch (error) {
      console.error('[UniAppStorage] Clear error:', error)
      throw new Error('Failed to clear storage')
    }
  }

  /**
   * 获取所有键
   */
  keys(): string[] {
    try {
      const info = uni.getStorageInfoSync()
      const allKeys = info.keys || []

      // 过滤出带有前缀的键,并移除前缀
      return allKeys
        .filter((key: string) => key.startsWith(this.prefix))
        .map((key: string) => key.substring(this.prefix.length))
    } catch (error) {
      console.error('[UniAppStorage] Keys error:', error)
      return []
    }
  }

  /**
   * 检查键是否存在
   */
  hasItem(key: string): boolean {
    try {
      const fullKey = this.getKey(key)
      const value = uni.getStorageSync(fullKey)
      return value !== undefined && value !== null && value !== ''
    } catch (error) {
      console.error('[UniAppStorage] Has error:', error)
      return false
    }
  }
}
